import { FilterFilled, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Input,
  InputRef,
  Select,
  TableColumnsType,
  TableColumnType,
  TreeSelect,
} from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import dayjs from "dayjs";
import { Dispatch, SetStateAction, useCallback, useMemo, useRef } from "react";
import { DropdownIcon } from "src/assets/icons/DropdownIcon";
import { TRANSACTION_STATUSES_ARRAY } from "src/components/constants";

import { Filters } from "src/components/TransactionsGrid/TransactionsGrid";
import { ClaimArg, useCheckAccess } from "src/HOC/withRBAC";

export type DataColumnConfig<T> = {
  [DataIndex in keyof T]?: {
    searchType?:
      | "text"
      | "date"
      | "select"
      | "multiSelect"
      | "textWithSelect"
      | "tree";
    options?: any[];
    claims?: ClaimArg;
  };
};

function useTransformDataColumns<T extends object>(
  columns: TableColumnsType<T>,
  config: DataColumnConfig<T>,
  filtersInfo: Filters<T>,
  onFilterChange: Dispatch<SetStateAction<Filters<T>>>,
) {
  const checkAccess = useCheckAccess();
  const columnsToBeTransformed = Object.keys(config) as (keyof T)[];
  const searchInput = useRef<InputRef>(null);

  const handleSearch = useCallback(
    (
      selectedKeys: string[],
      confirm: FilterDropdownProps["confirm"],
      dataIndex: string | number,
    ) => {
      confirm();

      const searchType = config[dataIndex as keyof T]?.searchType;
      if (searchType === "multiSelect" || searchType === "tree") {
        onFilterChange((curFilters) => ({
          ...curFilters,
          [dataIndex]: selectedKeys,
        }));
        return;
      }

      const filterValue = selectedKeys[1]
        ? `${selectedKeys[0]}<>${selectedKeys[1]}`
        : selectedKeys[0];

      onFilterChange((curFilters) => ({
        ...curFilters,
        [dataIndex]: [filterValue],
      }));
    },
    [onFilterChange, config],
  );

  const handleReset = useCallback(
    (dataIndex: string | number, confirm: FilterDropdownProps["confirm"]) => {
      const newFilters = { ...filtersInfo };
      delete newFilters[dataIndex];
      onFilterChange(newFilters);
      confirm();
    },
    [filtersInfo, onFilterChange],
  );

  const getColumnSearchProps = useCallback(
    (
      dataIndex: keyof T,
      searchType?:
        | "text"
        | "date"
        | "select"
        | "multiSelect"
        | "textWithSelect"
        | "tree",
      claims?: ClaimArg,
      options?: { label: string; value: string }[],
    ): TableColumnType<T> => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => {
        if (!searchType) return null;

        const searchComponents: Record<string, JSX.Element> = {
          text: (
            <Input
              ref={searchInput}
              placeholder={`Search ${String(dataIndex)}`}
              value={selectedKeys[0]}
              className="px-3 py-2 border-2"
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                handleSearch(
                  selectedKeys as string[],
                  confirm,
                  dataIndex as string,
                )
              }
              style={{ width: 250, display: "block" }}
            />
          ),
          textWithSelect: (
            <div className="flex gap-2 w-full">
              <Select
                placeholder={"Type"}
                className="custom-select [&_.ant-select-selector]:!h-10"
                value={selectedKeys[1]}
                defaultValue={"=="}
                onChange={(value) =>
                  setSelectedKeys([selectedKeys[0], value ? value : ""])
                }
                style={{ width: 100, display: "block" }}
                suffixIcon={<DropdownIcon />}
              >
                <Select.Option key={"equal"} value={"=="}>
                  Equal
                </Select.Option>
                <Select.Option key={"notEqual"} value={"!="}>
                  Not Equal
                </Select.Option>
              </Select>
              <Input
                ref={searchInput}
                placeholder={`Search ${String(dataIndex)}`}
                value={selectedKeys[0]}
                className="px-3 py-2 border-2"
                onChange={(e) =>
                  setSelectedKeys(
                    e.target.value ? [e.target.value, selectedKeys[1]] : [],
                  )
                }
                onPressEnter={() =>
                  handleSearch(
                    selectedKeys as string[],
                    confirm,
                    dataIndex as string,
                  )
                }
                style={{ width: 250, display: "block" }}
              />
            </div>
          ),
          select: (
            <Select
              placeholder={`Search ${String(dataIndex)}`}
              className="custom-select [&_.ant-select-selector]:!h-10"
              value={selectedKeys[0]}
              onChange={(value) => setSelectedKeys(value ? [value] : [])}
              style={{ width: 250, display: "block" }}
              suffixIcon={<DropdownIcon />}
            >
              {TRANSACTION_STATUSES_ARRAY.map(({ id, name }) => (
                <Select.Option key={id} value={id}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          ),
          multiSelect: (
            <Select
              mode="multiple"
              placeholder={`Search ${String(dataIndex)}`}
              value={selectedKeys as string[]}
              onChange={(value) => setSelectedKeys(value || [])}
              style={{ width: 250, display: "block" }}
              suffixIcon={<DropdownIcon />}
              placement="topLeft"
            >
              {options?.map(({ label, value }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          ),
          tree: (
            <TreeSelect
              className="w-64"
              showSearch
              placeholder={`Search ${String(dataIndex)}`}
              value={selectedKeys as string[]}
              onChange={(value) => setSelectedKeys(value || [])}
              allowClear
              multiple
              treeData={options}
            />
          ),
          date: (
            <>
              <DatePicker
                placeholder="Start Date"
                className="border-2 h-10 w-full flex items-center justify-center"
                format={"YYYY-MM-DD HH:mm"}
                value={
                  selectedKeys[0] && selectedKeys[0].toString().split("<>")[0]
                    ? dayjs(selectedKeys[0].toString().split("<>")[0])
                    : null
                }
                onChange={(date) => {
                  const endDate = selectedKeys[0]
                    ? selectedKeys[0].toString().split("<>")[1]
                    : "";
                  const formattedDate = date
                    ? dayjs(date).second(0).millisecond(0).toISOString()
                    : "";
                  setSelectedKeys(date ? [`${formattedDate}<>${endDate}`] : []);
                }}
                onOk={(date) => {
                  const endDate = selectedKeys[0]
                    ? selectedKeys[0].toString().split("<>")[1]
                    : "";
                  const formattedDate = date
                    ? dayjs(date).second(0).millisecond(0).toISOString()
                    : "";
                  setSelectedKeys(date ? [`${formattedDate}<>${endDate}`] : []);
                }}
                style={{ width: 250, display: "block" }}
                showTime={{
                  format: "HH:mm",
                  use12Hours: true,
                }}
              />
              <DatePicker
                placeholder="End Date"
                className="border-2 h-10 w-full flex items-center justify-center"
                format={"YYYY-MM-DD HH:mm"}
                value={
                  selectedKeys[0] && selectedKeys[0].toString().split("<>")[1]
                    ? dayjs(selectedKeys[0].toString().split("<>")[1])
                    : null
                }
                onChange={(date) => {
                  const startDate = selectedKeys[0]
                    ? selectedKeys[0].toString().split("<>")[0]
                    : "";
                  const formattedDate = date
                    ? dayjs(date).second(0).millisecond(0).toISOString()
                    : "";
                  setSelectedKeys(
                    date ? [`${startDate}<>${formattedDate}`] : [],
                  );
                }}
                onOk={(date) => {
                  const startDate = selectedKeys[0]
                    ? selectedKeys[0].toString().split("<>")[0]
                    : "";
                  const formattedDate = date
                    ? dayjs(date).second(0).millisecond(0).toISOString()
                    : "";
                  setSelectedKeys(
                    date ? [`${startDate}<>${formattedDate}`] : [],
                  );
                }}
                style={{ width: 250, display: "block" }}
                showTime={{
                  format: "HH:mm",
                  use12Hours: true,
                }}
              />
            </>
          ),
        };

        return (
          <div className="p-4 flex flex-col gap-2 items-center justify-center">
            {searchComponents[searchType]}
            <div className="w-full flex items-center justify-center gap-2">
              <Button
                className="w-full rounded-lg text-center bg-primary px-2 py-1 h-full text-16 text-white"
                onClick={() => {
                  handleSearch(
                    selectedKeys as string[],
                    confirm,
                    dataIndex as string,
                  );
                }}
                size="small"
                style={{ width: 90 }}
              >
                Search
              </Button>
              <Button
                className="w-full rounded-lg border-1 border-solid border-primary bg-transparent text-16 font-medium px-2 py-1 h-full text-primary hover:bg-primary/10 disabled:grayscale"
                size="small"
                style={{ width: 90 }}
                onClick={() => {
                  setSelectedKeys([]);
                  handleReset(dataIndex as string, confirm);
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => {
        if (!searchType) return null;
        return (
          <>
            {searchType === "text" ? (
              <SearchOutlined
                style={{ color: filtered ? "#1677ff" : undefined }}
              />
            ) : (
              <FilterFilled
                style={{ color: filtered ? "#1677ff" : undefined }}
              />
            )}
          </>
        );
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
      filteredValue: filtersInfo[dataIndex] || null,
      hidden: !checkAccess(claims),
    }),
    [checkAccess, filtersInfo, handleReset, handleSearch],
  );

  const dataColumns = useMemo(
    () =>
      columns.map((column) => {
        if (
          columnsToBeTransformed.some(
            (columnToBeTransformed) => columnToBeTransformed === column.key,
          )
        ) {
          const key = column.key;
          if (key !== undefined && config.hasOwnProperty(key)) {
            const typedKey = key as keyof T;
            return {
              ...column,
              ...getColumnSearchProps(
                typedKey,
                config[typedKey]?.searchType,
                config[typedKey]?.claims,
                config[typedKey]?.options,
              ),
            };
          }
        }
        return column;
      }),
    [columns, config, columnsToBeTransformed, getColumnSearchProps],
  );

  return dataColumns;
}

export default useTransformDataColumns;
